/**
 * Serializes cart API mutations so only one request is in-flight at a time.
 * Prevents race conditions between quantity changes, sample updates, and
 * third-party app cart modifications (BOGOS, Rebuy, etc.).
 *
 * Also provides a per-line debounce for user-initiated quantity changes so
 * rapid clicks/scrubs collapse into a single request. Checkout flushes and
 * drains the queue before navigating (see `guardCheckout` in cart-drawer),
 * so there is no global "cart is busy" indicator — callers either handle
 * waiting themselves or rely on optimistic UI.
 */

let queue: Promise<void> = Promise.resolve()
let pendingCount = 0

/**
 * Enqueue a cart operation. The provided function will only execute once
 * all previously enqueued operations have completed. Errors are swallowed
 * at the queue level so one failure doesn't block subsequent operations
 * (callers that care should attach their own .catch on the returned promise).
 */
export function enqueueCartOperation<T>(operation: () => Promise<T>): Promise<T> {
	pendingCount++

	const result = queue.then(() => operation())

	queue = result.then(
		() => { pendingCount-- },
		() => { pendingCount-- },
	)

	return result
}

// ---------------------------------------------------------------------------
// Per-item debounced cart change
// ---------------------------------------------------------------------------

const DEBOUNCE_MS = 400

type ExecuteChangeFn = (line: string, quantity: string, lineItemKey: string | undefined) => Promise<void>

interface PendingChange {
	timer: ReturnType<typeof setTimeout>
	operation: () => Promise<void>
}

const pendingChanges: Map<string, PendingChange> = new Map()

function firePendingChange(pending: PendingChange): void {
	void enqueueCartOperation(pending.operation)
}

/**
 * Debounce a cart quantity change per line item. Rapid changes to the same
 * item collapse into a single request with the final quantity. Changes to
 * different items debounce independently and serialize through the queue.
 *
 * @param key - Unique identifier for the line (lineItemKey or line index)
 * @param line - Positional line index (for DOM targeting)
 * @param quantity - Desired quantity (as string, matching Shopify API)
 * @param lineItemKey - Stable line item key (preferred over positional index)
 * @param executeChange - Callback that performs the actual /cart/change fetch.
 *   Receives (line, quantity, lineItemKey) and must return a Promise.
 */
export function enqueueDebouncedCartChange(
	key: string,
	line: string,
	quantity: string,
	lineItemKey: string | undefined,
	executeChange: ExecuteChangeFn,
): void {
	enqueueDebouncedCartOperation(key, () => executeChange(line, quantity, lineItemKey))
}

/**
 * Debounce any cart operation by key (last call wins). Flushed by checkout.
 * Key must not collide with line item keys.
 */
export function enqueueDebouncedCartOperation(
	key: string,
	operation: () => Promise<void>,
): void {
	const existing = pendingChanges.get(key)
	if (existing) {
		clearTimeout(existing.timer)
	}

	const timer = setTimeout(() => {
		const pending = pendingChanges.get(key)
		pendingChanges.delete(key)
		if (pending) firePendingChange(pending)
	}, DEBOUNCE_MS)

	pendingChanges.set(key, { timer, operation })
}

/**
 * Cancel all pending debounced changes. Used when a third-party app
 * (e.g. Rebuy) modifies the cart and we need to abandon optimistic state.
 */
export function cancelPendingDebounces(): void {
	for (const [, pending] of pendingChanges) {
		clearTimeout(pending.timer)
	}
	pendingChanges.clear()
}

/**
 * Immediately fire all pending debounced changes (skip their timers).
 * Used by checkout to ensure all optimistic changes are submitted before
 * we check server state.
 */
export function flushPendingDebounces(): void {
	for (const [key, pending] of pendingChanges) {
		clearTimeout(pending.timer)
		pendingChanges.delete(key)
		firePendingChange(pending)
	}
}

/**
 * Returns a promise that resolves when the queue is fully drained
 * (no in-flight operations and no pending debounces).
 */
export function waitForQueueDrain(): Promise<void> {
	if (pendingCount === 0 && pendingChanges.size === 0) {
		return Promise.resolve()
	}
	return queue
}
